import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Aufteilungsschluessel } from '../../domain/model/datenabfrage/aufteilungsschluessel';
import { Datenabfrage } from '../../domain/model/datenabfrage/datenabfrage';
import { ArchivierteDatenabfrage } from '../../domain/model/datenabfrage/archivierte-datenabfrage';
import { NetzbetreiberAktiveDatenabfrageAssignment } from '../../domain/model/datenabfrage/netzbetreiber-aktive-datenabfrage-assignment';

@Injectable({
    providedIn: 'root',
})
export class DatenabfrageService {
    private http = inject(HttpClient);

    // for a Westnetz-User thats requests a list of all Datenabfragen
    private getAktiveDatenAbfrageForWestnetz(): Observable<Datenabfrage | null> {
        return this.http.get<Datenabfrage>(environment.API_URL +
            '/datenabfrage/aktiveDatenabfrage/westnetz');
    }

    private getAktiveDatenAbfrageForStadtwerk(stadtwerkeId: string): Observable<Datenabfrage | null> {
        return this.http.get<Datenabfrage>(environment.API_URL + '/datenabfrage/aktiveDatenabfrage/' +
            stadtwerkeId);
    }

    public getAktiveDatenAbfrage(isWestnetzUserOrAdmin: boolean, stadtwerkeId: string): Observable<Datenabfrage> {
        if (isWestnetzUserOrAdmin) {
            return this.getAktiveDatenAbfrageForWestnetz();
        } else {
            return this.getAktiveDatenAbfrageForStadtwerk(stadtwerkeId);
        }
    }

    public getArchivierteDatenabfragen(isWestnetzUserOrAdmin: boolean, stadtwerkeId: string): Observable<ArchivierteDatenabfrage[] | null> {
        if (isWestnetzUserOrAdmin) {
            return this.getArchivierteDatenabfragenForWestnetz();
        } else {
            return this.getArchivierteDatenabfragenForStadtwerk(stadtwerkeId);
        }
    }

    private getArchivierteDatenabfragenForWestnetz(): Observable<ArchivierteDatenabfrage[]> {
        return this.http.get<ArchivierteDatenabfrage[]>(environment.API_URL +
            '/datenabfrage/archivierte/westnetz');
    }


    private getArchivierteDatenabfragenForStadtwerk(stadtwerkeId: string): Observable<ArchivierteDatenabfrage[]> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);
        return this.http.get<ArchivierteDatenabfrage[]>(environment.API_URL +
            '/datenabfrage/archivierte', { params });
    }

    public getNetzbetreibersWithAssignmentsToAktiveDatenAbfrage(): Observable<NetzbetreiberAktiveDatenabfrageAssignment[]> {
        return this.http.get<NetzbetreiberAktiveDatenabfrageAssignment[]>(environment.API_URL +
            '/datenabfrage/aktiveDatenabfrage/netzbetreibersWithAssignmentsToAktiveDatenAbfrage');
    }

    public deleteAndRemoveAtsAndNetzbetreiberAssignment(netzbetreiberId: string): Observable<void> {
        let params = new HttpParams();
        params = params.set('netzbetreiberId', netzbetreiberId);
        return this.http.post<void>(environment.API_URL +
            '/admin/datenabfrage/aktiveDatenabfrage/deleteAndRemoveAtsAndNetzbetreiberAssignment',
        null, { params });
    }

    public addNetzbetreiberAssignment(netzbetreiberId: string): Observable<void> {
        let params = new HttpParams();
        params = params.set('netzbetreiberId', netzbetreiberId);
        return this.http.post<void>(environment.API_URL +
            '/admin/datenabfrage/aktiveDatenabfrage/addNetzbetreiberAssignment',
        null, { params });
    }

    // for the Westnetz-Admin that creates a new Abfrage
    public createDatenabfrage(datenabfrage: Datenabfrage): Observable<Datenabfrage> {
        // treat the dates as if they would have been set in the UTC
        datenabfrage.abgabefrist = this.removeTimeZone(datenabfrage.abgabefrist);
        datenabfrage.referenzzeitraumErzeugerleistung =
            this.removeTimeZone(datenabfrage.referenzzeitraumErzeugerleistung);
        datenabfrage.referenzzeitraumEndverbraucherlast =
            this.removeTimeZone(datenabfrage.referenzzeitraumEndverbraucherlast);
        return this.http.post<Datenabfrage>(environment.API_URL + '/admin/datenabfrage', datenabfrage);
    }

    // for both Westnetz-User and Netzbetreiber that want to load the results of a Datenabfrage
    public getAufteilungsschluessel(datenabfrageId: number, stadtwerkeId: string | undefined):
        Observable<Aufteilungsschluessel[]> {
        let params = new HttpParams();
        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        return this.http.get<Aufteilungsschluessel[]>(
            environment.API_URL + '/datenabfrage/aktive/' + datenabfrageId + '/aufteilungsschluessel',
            { params },
        );
    }


    public saveAufteilungsschluessel(
        datenabfrageId: number,
        stadtwerkeId: string,
        aufteilungsschluessel: Aufteilungsschluessel,
    ): Observable<Aufteilungsschluessel> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);

        return this.http.post<Aufteilungsschluessel>(
            environment.API_URL + '/datenabfrage/aktive/' + datenabfrageId + '/aufteilungsschluessel',
            aufteilungsschluessel,
            { params },
        );
    }

    public archiviereAktiveDatenAbfrage(): Observable<Datenabfrage> {
        return this.http.post<Datenabfrage>(environment.API_URL + '/admin/datenabfrage/archivieren', null);
    }

    public deleteAktiveDatenabfrage(id: number) {
        return this.http.delete<any>(environment.API_URL + '/admin/datenabfrage/aktive/' + id);
    }

    public deleteArchivierteDatenabfrage(id: number) {
        return this.http.delete<any>(environment.API_URL + '/admin/datenabfrage/archiviert/' + id);
    }

    private removeTimeZone(date: Date): Date {
        return new Date(Date.UTC((date as any).year(),
            (date as any).month(), (date as any).date(), 0, 0, 0));
    }
}
